import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { capitalize } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { DateTime, Duration } from "luxon";
import {
  Intervention,
  Objective,
  ObjectiveAssignment,
  State,
  Task,
} from "protos/rtc/jobs";
import {
  InterventionUpdate,
  ObjectiveAssignmentUpdate,
  ObjectiveUpdate,
  TaskUpdate,
  UpdateType,
} from "protos/rtc/web_socket";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { jobStateIcon, jobStateIconColor } from "common/utils/job";
import { MuiIcon } from "common/components/types";
import { objectiveTypeToLocaleString } from "common/utils/enumTranslations";
import { RigCompatibilityStatus } from "protos/portal/atk_equipment";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { rtcLocatorApi } from "common/state/rtcLocatorApi";
import { useCurrentTime } from "common/hooks/useCurrentTime";
import { useDispatch } from "react-redux";
import {
  useInterventionByRobotSubscription,
  useObjectiveSubscription,
} from "common/hooks/useLiveJobData";
import { useLatest } from "common/hooks/useLatest";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/ContentPasteGo";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorIcon from "@mui/icons-material/Dangerous";
import InterventionIcon from "@mui/icons-material/NewReleases";
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import WarningIcon from "@mui/icons-material/WarningAmber";

interface ObjectiveDetailsProps {
  objective: Objective;
}
export const ObjectiveDetails: FC<ObjectiveDetailsProps> = ({ objective }) => {
  const { t } = useTranslation();

  const [mutateAssignment, { isLoading: assignmentLoading }] =
    useMutationPopups(rtcJobsApi.endpoints.setAssignment.useMutation());

  const [removeAssignment, { isLoading: removalLoading }] = useMutationPopups(
    rtcJobsApi.endpoints.removeAssignment.useMutation()
  );

  const { data: pathPlan } = rtcJobsApi.endpoints.getObjectivePathPlan.useQuery(
    {
      objectiveId: objective.id,
    }
  );
  const [copiedPathPlan, setCopiedPathPlan] = useState<boolean>(false);
  const resetCopyRef = useRef<NodeJS.Timeout>();
  const copyPathPlan = (): void => {
    navigator.clipboard.writeText(JSON.stringify(pathPlan));
    setCopiedPathPlan(true);
    clearTimeout(resetCopyRef.current);
    resetCopyRef.current = setTimeout(() => {
      setCopiedPathPlan(false);
    }, 5000);
  };

  const { data: configuredJob } =
    rtcJobsApi.endpoints.getConfiguredJob.useQuery({
      id: objective.jobId.toString(),
    });
  const job = configuredJob?.job;
  const groundPrepConfig = configuredJob?.groundPrep;
  const [objectiveResponse, setObjective] = useState<Objective>();
  const [interventions, setInterventions] = useState<Intervention[]>();
  const [tasks, setTasks] = useState<Task[]>();
  const [listObjectiveTasks] =
    rtcJobsApi.endpoints.listObjectiveTasks.useLazyQuery();
  const [getObjective] = rtcJobsApi.endpoints.getObjective.useLazyQuery();
  const [listObjectiveInterventions] =
    rtcJobsApi.endpoints.listObjectiveInterventions.useLazyQuery();
  const [assignment, setAssignment] = useState<ObjectiveAssignment>();

  // A created task can shift its siblings' positions without publishing them,
  // so reread the ordered list.
  const objectiveIdRef = useLatest(objective.id);
  const relistTasks = useCallback(async () => {
    const objectiveId = objectiveIdRef.current;
    const { data } = await listObjectiveTasks({ objectiveId });
    if (data && objectiveId === objectiveIdRef.current) {
      setTasks(data.tasks);
    }
  }, [listObjectiveTasks, objectiveIdRef]);

  const onTaskUpdate = useCallback(
    (t: TaskUpdate) => {
      const task = t.task;
      if (!task) {
        return;
      }
      switch (t.type) {
        case UpdateType.UPDATE_TYPE_CREATE: {
          relistTasks();
          break;
        }
        case UpdateType.UPDATE_TYPE_DELETE: {
          setTasks((ts) => ts?.filter((ot) => ot.id !== task.id));
          break;
        }
        default: {
          setTasks((ts) => ts?.map((ot) => (ot.id === task.id ? task : ot)));
          break;
        }
      }
    },
    [relistTasks]
  );
  const onObjectiveUpdate = useCallback((o: ObjectiveUpdate) => {
    setObjective(o.objective);
  }, []);
  const onAssignmentUpdate = useCallback((a: ObjectiveAssignmentUpdate) => {
    if (a.type === UpdateType.UPDATE_TYPE_DELETE) {
      setAssignment(undefined);
    } else {
      setAssignment(a.objectiveAssignment);
    }
  }, []);
  useObjectiveSubscription({
    objectiveId: objective.id.toString(),
    onObjectiveUpdate,
    onTaskUpdate,
    onAssignmentUpdate,
  });

  // interventions come from the robot working the objective
  const tasksRef = useLatest(tasks);
  const onInterventionUpdate = useCallback(
    (u: InterventionUpdate) => {
      const intervention = u.intervention;
      if (
        !intervention ||
        !tasksRef.current?.some((task) => task.id === intervention.taskId)
      ) {
        return;
      }
      setInterventions((is) => {
        const others = (is ?? []).filter((i) => i.id !== intervention.id);
        return u.type === UpdateType.UPDATE_TYPE_DELETE
          ? others
          : [...others, intervention];
      });
    },
    [tasksRef]
  );
  useInterventionByRobotSubscription({
    serial: assignment?.robotSerial ?? "",
    onInterventionUpdate,
    skip: !assignment?.robotSerial,
  });

  const [updateObjective] = useMutationPopups(
    rtcJobsApi.endpoints.updateObjective.useMutation()
  );
  const details = objectiveResponse ?? objective;
  const {
    data: availableTractors,
    isLoading: tractorsLoading,
    isFetching: tractorsFetching,
  } = useQueryPopups(
    rtcLocatorApi.endpoints.listRobots.useQuery({
      serials: job?.robotWhitelist?.entries.map((r) => r.robotSerial),
    })
  );

  const { data: compatibleRobots } = useQueryPopups(
    atkEquipmentApi.endpoints.listCompatibleRobots.useQuery(
      {
        serials: availableTractors?.robots.map((r) => r.serial) ?? [],
        snapshotId: groundPrepConfig?.rigSnapshotId ?? "",
      },
      {
        skip: !(availableTractors?.robots && groundPrepConfig?.rigSnapshotId),
      }
    )
  );

  const assignmentIsLoading =
    assignmentLoading || tractorsLoading || removalLoading || tractorsFetching;

  // initial fetch
  useEffect(() => {
    const ac = new AbortController();
    const fetch = async (): Promise<void> => {
      const { data: tresponse } = await listObjectiveTasks({
        objectiveId: objective.id,
      });
      if (ac.signal.aborted) {
        return;
      }
      setTasks(tresponse?.tasks);
      const { data: oresponse } = await getObjective({
        id: objective.id,
      });
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (ac.signal.aborted) {
        return;
      }
      setObjective(oresponse);
      setAssignment(oresponse?.assignment);
      const { data: iresponse } = await listObjectiveInterventions({
        objectiveId: objective.id.toString(),
      });
      setInterventions(iresponse?.interventions);
    };
    fetch();
    return () => ac.abort();
  }, [getObjective, listObjectiveTasks, listObjectiveInterventions, objective]);

  return (
    <div className="flex flex-col gap-4 h-full">
      <Typography className="px-4 text-slate-400">
        {details.description}
      </Typography>
      <div className="flex">
        <FormControl fullWidth className="mx-4 flex">
          <InputLabel id="assignment-label">
            {capitalize(t("models.autotractor.assignment_one"))}
          </InputLabel>
          <Select
            fullWidth
            labelId="assignment-label"
            label={
              assignmentIsLoading
                ? t("utils.descriptors.loading")
                : capitalize(t("models.autotractor.assignment_one"))
            }
            value={assignment?.robotSerial ?? ""}
            type="string"
            disabled={assignmentIsLoading}
            onChange={(e) =>
              e.target.value &&
              mutateAssignment({
                objectiveId: details.id,
                robotSerial: e.target.value,
              })
            }
            startAdornment={
              <div className="flex gap-2 mr-2">
                <AssignmentIcon color="info" className="flex my-auto" />
                {assignmentIsLoading && (
                  <CircularProgress
                    className="flex my-auto ml-2 h-6 w-6"
                    variant="indeterminate"
                  />
                )}
              </div>
            }
            endAdornment={
              assignment?.robotSerial && (
                <IconButton
                  className="mr-4"
                  onClick={() =>
                    removeAssignment({
                      objectiveId: details.id,
                    })
                  }
                  disabled={assignmentIsLoading}
                  color="info"
                >
                  <CloseIcon />
                </IconButton>
              )
            }
          >
            {availableTractors?.robots.map((tractor) => {
              let disabled: boolean = true;
              let detailText: string | undefined;
              let Icon: MuiIcon | undefined;

              if (groundPrepConfig?.rigSnapshotId === undefined) {
                // currently, assume that tractors are always compatible with
                // any job that's not a ground prep job using the equipment
                // service
                disabled = false;
              } else {
                switch (compatibleRobots?.robots[tractor.serial]?.status) {
                  case RigCompatibilityStatus.COMPATIBILITY_STATUS_OK: {
                    disabled = false;
                    break;
                  }
                  case RigCompatibilityStatus.COMPATIBILITY_STATUS_WARNING: {
                    disabled = false;
                    Icon = WarningIcon;
                    detailText = t(
                      "views.autotractor.jobDetails.assignmentDetailText.compatibilityWarning"
                    );
                    break;
                  }
                  case RigCompatibilityStatus.COMPATIBILITY_STATUS_ERROR: {
                    disabled = true;
                    Icon = ErrorIcon;
                    detailText = t(
                      "views.autotractor.jobDetails.assignmentDetailText.compatibilityError"
                    );
                    break;
                  }
                  case RigCompatibilityStatus.COMPATIBILITY_STATUS_ERROR_SWITCH_RIG: {
                    disabled = true;
                    Icon = ErrorIcon;
                    detailText = t(
                      "views.autotractor.jobDetails.assignmentDetailText.compatibilityErrorSwitchRig"
                    );
                    break;
                  }
                  default: {
                    // compatibility check returned an UNRECOGNIZED value, no value
                    // at all, or is still loading
                    disabled = true;
                    detailText = t("components.Loading.placeholder");
                  }
                }
              }

              return (
                <MenuItem
                  key={tractor.serial}
                  value={tractor.serial}
                  disabled={disabled}
                >
                  <div className="flex flex-col">
                    <div className="flex flex-row items-center">
                      {Icon && <Icon className="mr-1" />}
                      <Typography>{tractor.serial}</Typography>
                    </div>
                    {detailText && (
                      <Typography
                        variant="body2"
                        className="text-wrap max-w-[20vw] ml-2"
                      >
                        {detailText}
                      </Typography>
                    )}
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
      <div className="flex justify-between px-4">
        <Typography>{t("utils.descriptors.type_one")}</Typography>
        <Typography>{objectiveTypeToLocaleString(t, details.type)}</Typography>
      </div>
      <div className="flex justify-between px-4">
        <AccessTimeIcon />
        <TotalTaskTime tasks={tasks} />
      </div>
      <div className="flex gap-1 mx-auto">
        <Button
          disabled={!pathPlan}
          onClick={copyPathPlan}
          size="small"
          color="secondary"
          startIcon={copiedPathPlan ? <CheckIcon /> : <ContentCopyIcon />}
        >
          {copiedPathPlan
            ? t("components.CopyToClipboardButton.copied")
            : t("views.autotractor.actions.copyPathPlan")}
        </Button>
      </div>
      <div className="px-4">
        <div className="flex justify-between">
          <Typography>{t("utils.descriptors.progress")}</Typography>
          <Typography>{`${details.progressPercent}${t(
            "utils.units.%"
          )}`}</Typography>
        </div>
        <LinearProgress
          variant="determinate"
          className="px-4 h-5"
          color="success"
          value={details.progressPercent}
        />
      </div>
      <ObjectiveTimeline
        objectiveId={objective.id}
        tasks={tasks}
        interventions={interventions}
      />
      <div className="flex mt-auto p-2">
        <Button
          variant="contained"
          fullWidth
          disabled={details.progressPercent === 100}
          onClick={() =>
            updateObjective({ id: details.id, progressPercent: 100 })
          }
        >
          {t("views.autotractor.actions.markComplete")}
        </Button>
      </div>
    </div>
  );
};

interface TotalTaskTimeProps {
  tasks?: Task[];
}
// owns the 1s clock so the ticking doesn't re-render the whole details panel
const TotalTaskTime: FC<TotalTaskTimeProps> = ({ tasks }) => {
  const { t, i18n } = useTranslation();
  const now = useCurrentTime(1000);
  const totalTaskTime: Duration | "not-started" = useMemo(() => {
    let startedAt: string | undefined;
    let endedAt: string | undefined;
    if (tasks?.length === 0 || !tasks) {
      return Duration.fromMillis(0, { locale: i18n.language });
    } else if (tasks.length === 1) {
      startedAt = tasks.at(0)?.startedAt;
      endedAt = tasks.at(0)?.endedAt;
    } else {
      startedAt = tasks.at(0)?.startedAt;
      endedAt = tasks.at(-1)?.endedAt;
    }
    if (!startedAt) {
      return "not-started";
    }
    const start = DateTime.fromISO(startedAt, { locale: i18n.language });
    if (endedAt) {
      const end = DateTime.fromISO(endedAt, { locale: i18n.language });
      return end.setLocale(i18n.language).diff(start);
    }
    return now.setLocale(i18n.language).diff(start);
  }, [tasks, now, i18n]);
  return (
    <Typography>
      {totalTaskTime === "not-started"
        ? capitalize(t("utils.descriptors.notStarted"))
        : totalTaskTime
            .shiftTo("hours", "minutes", "seconds")
            .mapUnits(Math.floor)
            .toHuman({ unitDisplay: "short" })}
    </Typography>
  );
};

const DONE_STATES = new Set([State.COMPLETED, State.CANCELLED, State.FAILED]);

interface ObjectiveTimelineProps {
  objectiveId: Objective["id"];
  tasks?: Task[];
  interventions?: Intervention[];
}
const ObjectiveTimeline: FC<ObjectiveTimelineProps> = ({
  objectiveId,
  tasks,
  interventions,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [follow, setFollow] = useState(true);
  const [followedObjectiveId, setFollowedObjectiveId] = useState(objectiveId);
  if (objectiveId !== followedObjectiveId) {
    setFollowedObjectiveId(objectiveId);
    setFollow(true);
  }
  const anchorRef = useRef<HTMLDivElement>(null);
  const sequenceRef = useRef<HTMLDivElement>(null);
  const { anchor, sequenceId } = useMemo(() => {
    // sequences precede their children inline, so find the current task among leaves
    const leaves = tasks?.filter((task) => !task.sequence) ?? [];
    const current = leaves.findIndex((task) => !DONE_STATES.has(task.state));
    return {
      anchor: leaves[current - 1] ?? leaves[current],
      sequenceId: leaves[current]?.parentTaskId,
    };
  }, [tasks]);
  // the sticky sequence only overlaps the anchor when it precedes it
  const anchorUnderSequence = anchor?.parentTaskId === sequenceId;
  // open at the previous task, just below the current task's sticky sequence
  useLayoutEffect(() => {
    if (!follow) {
      return;
    }
    const row = anchorRef.current;
    const sequence = sequenceRef.current;
    row?.parentElement?.scrollTo({
      top:
        row.offsetTop -
        (anchorUnderSequence && sequence ? sequence.offsetHeight : 0),
    });
  }, [follow, anchor?.id, sequenceId, anchorUnderSequence]);
  return (
    <div className="flex flex-col flex-1 min-h-0">
      <FormControlLabel
        className="self-center"
        control={
          <Switch
            checked={follow}
            onChange={(e) => setFollow(e.target.checked)}
            color="secondary"
          />
        }
        label={t("views.autotractor.jobDetails.followCurrentTask")}
      />
      {/* user input only; scroll events also fire for our scrollTo and scroll anchoring */}
      <div
        className="relative px-4 flex-1 min-h-0 overflow-y-auto"
        onWheel={() => setFollow(false)}
        onTouchMove={() => setFollow(false)}
        onPointerDown={(e) => {
          // scrollbar drag
          if (
            e.target === e.currentTarget &&
            e.nativeEvent.offsetX >= e.currentTarget.clientWidth
          ) {
            setFollow(false);
          }
        }}
      >
        {tasks?.map((task) => {
          const linkedInterventions = interventions?.filter(
            (i) => i.taskId === task.id
          );
          const Icon = jobStateIcon(task.state);
          const iconColor = jobStateIconColor(task.state);
          let rowRef: React.RefObject<HTMLDivElement> | undefined;
          if (task.id === anchor?.id) {
            rowRef = anchorRef;
          } else if (task.id === sequenceId) {
            rowRef = sequenceRef;
          }
          return (
            <Paper
              key={task.id}
              ref={rowRef}
              square
              className={classes(
                "flex flex-col shadow-none",
                task.id === sequenceId && "sticky top-0 z-10"
              )}
            >
              {task.startedAt && (
                <div className="flex gap-2 ml-4">
                  <AccessTimeIcon fontSize="small" className="fill-gray-500" />
                  <Typography
                    variant="caption"
                    color="GrayText"
                    className="flex my-auto"
                  >
                    {DateTime.fromISO(task.startedAt).toLocaleString(
                      {
                        timeStyle: "medium",
                      },
                      { locale: i18n.language }
                    )}
                  </Typography>
                </div>
              )}
              <div className="flex break-auto-all">
                {
                  <Icon
                    className={classes(
                      "flex my-auto",
                      task.state === State.IN_PROGRESS && "animate-reverse-spin"
                    )}
                    color={iconColor}
                  />
                }
                <Button
                  onClick={() =>
                    dispatch(
                      jobExplorer.actions.setSelection({
                        type: JobSelectionType.TASK,
                        selection: task,
                      })
                    )
                  }
                  variant="text"
                  size="small"
                  fullWidth
                  color={iconColor}
                  className={classes(
                    task.state === State.COMPLETED &&
                      "line-through text-green-500",
                    task.state === State.FAILED && "text-red-500"
                  )}
                >
                  {task.name}
                </Button>
              </div>
              <div className="flex flex-col">
                {linkedInterventions?.map((i) => (
                  <div key={i.id} className="flex mx-4 gap-2">
                    <InterventionIcon
                      className={classes(
                        "flex my-auto h-4 w-4",
                        i.state === State.COMPLETED && "text-green-300"
                      )}
                    />
                    <div
                      className={classes(
                        "text-sm",
                        i.state === State.COMPLETED &&
                          "line-through text-green-300"
                      )}
                    >
                      {i.description}
                    </div>
                  </div>
                ))}
              </div>
              {task.endedAt && (
                <div className="flex gap-2 ml-4">
                  <AccessTimeIcon fontSize="small" className="fill-gray-500" />
                  <Typography
                    variant="caption"
                    color="GrayText"
                    className="flex my-auto"
                  >
                    {DateTime.fromISO(task.endedAt).toLocaleString(
                      {
                        timeStyle: "medium",
                      },
                      { locale: i18n.language }
                    )}
                  </Typography>
                </div>
              )}
              <Divider className="mx-4 py-1" />
            </Paper>
          );
        })}
      </div>
    </div>
  );
};
